<template>
  <v-container>
    <div class="d-flex flex-wrap justify-between align-center" style="gap: 10px">
      <h1>WordPress SSO Connections</h1>
      <v-btn color="primary" @click="showAddDialog = true">
        <v-icon start>mdi-plus</v-icon>
        Add WordPress Site
      </v-btn>
    </div>

    <v-card v-if="connections.length === 0 && !loading" class="mt-4">
      <v-card-text class="text-center py-8">
        <v-icon size="64" color="grey-lighten-1" class="mb-4">mdi-wordpress</v-icon>
        <h3 class="text-h6 mb-2">No WordPress connections</h3>
        <p class="text-body-2 text-medium-emphasis mb-4">
          Connect your WordPress sites to enable single sign-on from JAMS
        </p>
        <v-btn color="primary" @click="showAddDialog = true">
          <v-icon start>mdi-plus</v-icon>
          Add Your First WordPress Site
        </v-btn>
      </v-card-text>
    </v-card>

    <v-card v-else class="mt-4">
      <v-data-table
        :headers="headers"
        :items="connections"
        :loading="loading"
        item-value="id"
        class="elevation-1"
      >
        <template #item.wpUrl="{ item }">
          <a :href="item.wpUrl" target="_blank" class="text-decoration-none">
            {{ item.wpUrl }}
            <v-icon size="small" class="ml-1">mdi-open-in-new</v-icon>
          </a>
        </template>
        <template #item.wpUsername="{ item }">
          <div class="d-flex align-center">
            <v-avatar size="24" class="mr-2">
              <v-icon>mdi-account</v-icon>
            </v-avatar>
            {{ item.wpUsername }}
          </div>
        </template>
        <template #item.wpEmail="{ item }">
          <a :href="`mailto:${item.wpEmail}`" class="text-decoration-none">
            {{ item.wpEmail }}
          </a>
        </template>
        <template #item.userType="{ item }">
          <v-chip
            size="small"
            :color="item.userType === 'client' ? 'blue' : 'green'"
            variant="tonal"
          >
            {{ item.userType === 'client' ? 'Client' : 'Company' }}
          </v-chip>
        </template>
        <template #item.createdAt="{ item }">
          {{ formatDate(item.createdAt) }}
        </template>
        <template #item.actions="{ item }">
          <div class="d-flex" style="gap: 8px">
            <v-btn size="small" color="primary" variant="text" @click="openWordPress(item)">
              <v-icon start size="small">mdi-open-in-new</v-icon>
              Open
            </v-btn>
            <v-btn size="small" color="error" variant="text" @click="confirmDelete(item)">
              <v-icon start size="small">mdi-delete</v-icon>
              Disconnect
            </v-btn>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <!-- Add WordPress Site Dialog -->
    <v-dialog v-model="showAddDialog" max-width="600">
      <v-card>
        <v-card-title>
          <span class="text-h5">Add WordPress Site</span>
        </v-card-title>
        <v-card-text>
          <v-form ref="formRef" v-model="formValid">
            <v-text-field
              v-model="newConnection.wpUrl"
              label="WordPress Site URL"
              placeholder="https://example.com"
              :rules="[rules.required, rules.url]"
              prepend-inner-icon="mdi-web"
              class="mb-4"
            ></v-text-field>
            <v-alert type="info" variant="tonal" class="mb-4">
              <div class="text-body-2">
                <strong>How it works:</strong>
                <ol class="mt-2 ml-4">
                  <li>Click "Connect" to open WordPress in a new tab</li>
                  <li>Log in to WordPress if needed</li>
                  <li>WordPress will generate a token and return it to JAMS</li>
                  <li>Your connection will be saved automatically</li>
                </ol>
              </div>
            </v-alert>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="showAddDialog = false">Cancel</v-btn>
          <v-btn
            color="primary"
            :disabled="!formValid || !newConnection.wpUrl"
            :loading="connecting"
            @click="connectToWordPress"
          >
            Connect
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="showDeleteDialog" max-width="400">
      <v-card>
        <v-card-title class="text-h6">Disconnect WordPress Site?</v-card-title>
        <v-card-text>
          <p>Are you sure you want to disconnect from:</p>
          <p class="font-weight-bold">{{ connectionToDelete?.wpUrl }}</p>
          <p class="text-caption text-medium-emphasis">
            You can reconnect anytime by adding the site again.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="showDeleteDialog = false">Cancel</v-btn>
          <v-btn color="error" :loading="deleting" @click="deleteConnection">Disconnect</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar for notifications -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3000">
      {{ snackbar.message }}
      <template #actions>
        <v-btn variant="text" @click="snackbar.show = false">Close</v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import {
  getSSOConnections,
  createSSOConnection,
  deleteSSOConnection,
  type SSOConnection,
} from '@/api/accountsAdmin'
import { useUserStore } from '@/stores/userStore'

const userStore = useUserStore()

// Determine user type based on current user's role
const currentUserType = computed(() => {
  const role = userStore.role as string | null
  if (role && role.startsWith('client_')) {
    return 'client'
  }
  return 'company'
})

const connections = ref<SSOConnection[]>([])
const loading = ref(false)
const connecting = ref(false)
const deleting = ref(false)
const showAddDialog = ref(false)
const showDeleteDialog = ref(false)
const formValid = ref(false)
const connectionToDelete = ref<SSOConnection | null>(null)
const formRef = ref()

const newConnection = ref({
  wpUrl: '',
})

const snackbar = ref({
  show: false,
  message: '',
  color: 'success',
})

const headers = [
  { title: 'WordPress URL', key: 'wpUrl', sortable: true },
  { title: 'Username', key: 'wpUsername', sortable: true },
  { title: 'Email', key: 'wpEmail', sortable: true },
  { title: 'User Type', key: 'userType', sortable: true },
  { title: 'Connected', key: 'createdAt', sortable: true },
  { title: 'Actions', key: 'actions', sortable: false, align: 'end' as const },
]

const rules = {
  required: (value: string) => !!value || 'Required',
  url: (value: string) => {
    if (!value) return true
    try {
      new URL(value)
      return true
    } catch {
      return 'Must be a valid URL'
    }
  },
}

const loadConnections = async () => {
  loading.value = true
  try {
    const response = await getSSOConnections()
    connections.value = response.connections || []
  } catch (error: any) {
    showSnackbar(
      'Error loading connections: ' + (error.response?.data?.message || error.message),
      'error'
    )
  } finally {
    loading.value = false
  }
}

const connectToWordPress = () => {
  if (!formValid.value || !newConnection.value.wpUrl) return

  let wpUrl = newConnection.value.wpUrl.trim()
  if (!wpUrl.startsWith('http://') && !wpUrl.startsWith('https://')) {
    wpUrl = 'https://' + wpUrl
  }
  wpUrl = wpUrl.replace(/\/$/, '')

  // Use current user's type (determined from their role)
  const userType = currentUserType.value

  const callbackUrl = `${window.location.origin}/sso/wp/callback`
  const connectUrl = `${wpUrl}/sso/wp/connect?redirect_url=${encodeURIComponent(callbackUrl)}&user_type=${userType}`

  const newWindow = window.open(connectUrl, '_blank', 'width=800,height=600')

  const messageHandler = async (event: MessageEvent) => {
    if (event.origin !== wpUrl) {
      return
    }

    if (event.data.type === 'jams-sso-callback') {
      window.removeEventListener('message', messageHandler)
      newWindow?.close()

      const { token, username, email } = event.data

      if (!token || !username) {
        showSnackbar('Invalid response from WordPress. Missing required information.', 'error')
        return
      }

      const wpEmail = email || `${username}@wordpress.local`

      connecting.value = true
      try {
        await createSSOConnection({
          wpUrl,
          token,
          wpUsername: username,
          wpEmail: wpEmail,
          userType, // Use current user's type
        })
        showSnackbar('Successfully connected to WordPress!', 'success')
        showAddDialog.value = false
        newConnection.value.wpUrl = ''
        await loadConnections()
      } catch (error: any) {
        showSnackbar(
          'Error saving connection: ' + (error.response?.data?.message || error.message),
          'error'
        )
      } finally {
        connecting.value = false
      }
    }
  }

  window.addEventListener('message', messageHandler)

  const checkInterval = setInterval(() => {
    if (newWindow?.closed) {
      clearInterval(checkInterval)
      window.removeEventListener('message', messageHandler)
      setTimeout(() => {
        loadConnections()
      }, 1000)
    }
  }, 500)
}

const openWordPress = (connection: SSOConnection) => {
  const loginUrl = `${connection.wpUrl}/sso/wp/login?token=${encodeURIComponent(connection.token)}`
  window.open(loginUrl, '_blank')
}

const confirmDelete = (connection: SSOConnection) => {
  connectionToDelete.value = connection
  showDeleteDialog.value = true
}

const deleteConnection = async () => {
  if (!connectionToDelete.value) return

  deleting.value = true
  try {
    await deleteSSOConnection(connectionToDelete.value.id)
    showSnackbar('WordPress site disconnected successfully', 'success')
    showDeleteDialog.value = false
    connectionToDelete.value = null
    await loadConnections()
  } catch (error: any) {
    showSnackbar(
      'Error disconnecting: ' + (error.response?.data?.message || error.message),
      'error'
    )
  } finally {
    deleting.value = false
  }
}

const formatDate = (dateString: string) => {
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const showSnackbar = (message: string, color: 'success' | 'error' = 'success') => {
  snackbar.value = {
    show: true,
    message,
    color,
  }
}

onMounted(() => {
  loadConnections()

  const urlParams = new URLSearchParams(window.location.search)
  const callbackType = urlParams.get('type')

  if (callbackType === 'jams-sso-callback') {
    const token = urlParams.get('token')
    const username = urlParams.get('username')
    const email = urlParams.get('email') || ''
    const wpUrl = urlParams.get('wp_url')

    if (token && username && wpUrl) {
      window.history.replaceState({}, document.title, window.location.pathname)

      const wpEmail = email || `${username}@wordpress.local`
      // Use current user's type (determined from their role)
      const userType = currentUserType.value

      createSSOConnection({
        wpUrl: decodeURIComponent(wpUrl),
        token,
        wpUsername: username,
        wpEmail: wpEmail,
        userType,
      })
        .then(() => {
          showSnackbar('Successfully connected to WordPress!', 'success')
          loadConnections()
        })
        .catch((error: any) => {
          showSnackbar(
            'Error saving connection: ' + (error.response?.data?.message || error.message),
            'error'
          )
        })
    } else {
      showSnackbar('Invalid WordPress callback. Missing required information.', 'error')
      window.history.replaceState({}, document.title, window.location.pathname)
    }
  }
})
</script>

<style scoped>
.v-data-table {
  border-radius: 4px;
}
</style>
